import React, { useState } from 'react';
import { Table, Button, Space, Modal, Form, Input, InputNumber, Switch, message, Popconfirm, Select } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, TeamOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

interface Building {
  id: string;
  name: string;
  address: string;
  totalParkingSpots: number;
  availableSpots: number;
  isActive: boolean;
  users?: any[];
}

const BuildingsPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUsersModalOpen, setIsUsersModalOpen] = useState(false);
  const [editingBuilding, setEditingBuilding] = useState<Building | null>(null);
  const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [form] = Form.useForm();
  const [usersForm] = Form.useForm();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['buildings', includeInactive],
    queryFn: async () => {
      const response = await api.get(`/admin/buildings?includeInactive=${includeInactive}`);
      return response.data;
    }
  });

  const createMutation = useMutation({
    mutationFn: (values: Partial<Building>) => api.post('/admin/buildings', values),
    onSuccess: () => {
      message.success('Budynek dodany');
      queryClient.invalidateQueries({ queryKey: ['buildings'] });
      setIsModalOpen(false);
      form.resetFields();
    },
    onError: () => message.error('Błąd dodawania budynku'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, values }: { id: string; values: Partial<Building> }) => api.put(`/admin/buildings/${id}`, values),
    onSuccess: () => {
      message.success('Budynek zaktualizowany');
      queryClient.invalidateQueries({ queryKey: ['buildings'] });
      setIsModalOpen(false);
      setEditingBuilding(null);
      form.resetFields();
    },
    onError: () => message.error('Błąd aktualizacji'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/buildings/${id}`),
    onSuccess: () => {
      message.success('Budynek usunięty');
      queryClient.invalidateQueries({ queryKey: ['buildings'] });
    },
    onError: () => message.error('Błąd usuwania'),
  });

  const assignUsersMutation = useMutation({
    mutationFn: ({ buildingId, userIds }: { buildingId: string; userIds: string[] }) =>
      api.post(`/admin/buildings/${buildingId}/users`, { userIds }),
    onSuccess: () => {
      message.success('Użytkownicy przypisani');
      queryClient.invalidateQueries({ queryKey: ['buildings'] });
        setIsUsersModalOpen(false);
      },
      onError: () => message.error('Błąd przypisywania użytkowników'),
  });

  const handleOk = () => {
    form.validateFields().then((values) => {
      if (editingBuilding) {
        updateMutation.mutate({ id: editingBuilding.id, values });
      } else {
        createMutation.mutate(values);
      }
    });
  };

  const handleEdit = (building: Building) => {
    setEditingBuilding(building);
    form.setFieldsValue(building);
    setIsModalOpen(true);
  };

  const handleAssignUsers = (building: Building) => {
    setSelectedBuilding(building);
    usersForm.setFieldsValue({ userIds: building.users?.map((u: any) => u.id) || [] });
    setIsUsersModalOpen(true);
  };

  const columns = [
    { title: 'Nazwa', dataIndex: 'name', key: 'name' },
    { title: 'Adres', dataIndex: 'address', key: 'address' },
    {
      title: 'Miejsca parkingowe',
      key: 'parking',
      render: (_: any, record: Building) => (
        <span>{record.availableSpots} / {record.totalParkingSpots}</span>
      ),
    },
    {
      title: 'Aktywny',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean, record: Building) => (
        <Switch checked={isActive} onChange={(checked) => updateMutation.mutate({ id: record.id, values: { isActive: checked } })} />
      ),
    },
    {
      title: 'Akcje',
      key: 'actions',
      render: (_: any, record: Building) => (
        <Space>
          <Button icon={<TeamOutlined />} size="small" onClick={() => handleAssignUsers(record)} title="Przypisz użytkowników" />
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />
          <Popconfirm title="Czy na pewno usunąć?" onConfirm={() => deleteMutation.mutate(record.id)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h1>Budynki</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingBuilding(null); form.resetFields(); setIsModalOpen(true); }}>
          Dodaj budynek
        </Button>
      </div>
      <div style={{ marginBottom: 16 }}>
        <Switch checked={includeInactive} onChange={(checked) => setIncludeInactive(checked)} />
        <span style={{ marginLeft: 8 }}>Pokaż nieaktywne budynki</span>
      </div>
      <Table dataSource={data?.buildings || []} columns={columns} rowKey="id" loading={isLoading} />

      <Modal title={editingBuilding ? 'Edytuj budynek' : 'Dodaj budynek'} open={isModalOpen} onOk={handleOk} onCancel={() => { setIsModalOpen(false); setEditingBuilding(null); }}>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Nazwa budynku" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="address" label="Adres" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="totalParkingSpots" label="Liczba miejsc parkingowych" rules={[{ required: true }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="availableSpots" label="Dostępne miejsca">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Przypisz użytkowników" open={isUsersModalOpen} onCancel={() => setIsUsersModalOpen(false)}
        onOk={() => {
          usersForm.validateFields().then((values) => {
            assignUsersMutation.mutate({ buildingId: selectedBuilding!.id, userIds: values.userIds || [] });
          });
        }}
      >
        <Form form={usersForm} layout="vertical">
          <Form.Item name="userIds" label="Użytkownicy">
            <SelectUsers form={usersForm} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

const SelectUsers: React.FC<{ form: any }> = ({ form }) => {
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await api.get('/admin/users');
      return response.data;
    }
  });

  return (
    <Select
      mode="multiple"
      placeholder="Wybierz użytkowników"
      style={{ width: '100%' }}
      value={form.getFieldValue('userIds') || []}
      onChange={(values) => form.setFieldsValue({ userIds: values })}
    >
      {usersData?.users?.map((user: any) => (
        <Select.Option key={user.id} value={user.id}>
          {user.firstName} {user.lastName} ({user.role})
        </Select.Option>
      ))}
    </Select>
  );
};

export default BuildingsPage;